import type es from '../es/workspace';

/** `workspace` in English. Same keys as `es/workspace.ts`, enforced by the type. */
const workspace: typeof es = {
  dashboard: {
    loading: 'Loading your dashboard',
    title: 'Dashboard',
    reconnectGmail: 'Reconnect Gmail',
    changeCv: 'Change resume',
    appBanner: {
      title: 'TBit Jobs is now on your phone',
      lede: 'Know the moment an employer replies, and answer from wherever you are.',
      appStore: 'Download on the App Store',
      playStore: 'Get it on Google Play',
      playStoreSoon: 'Coming soon to Google Play',
      soon: 'Coming soon',
      dismiss: 'Close',
    },
    gmailDead: {
      title: 'Your Gmail got disconnected',
      body: 'Google no longer lets us send from your account, so your applications are on hold. Reconnect it and we will start applying for you again.',
    },
    scoreNudge: 'Your resume can get stronger. See how',
    sender: {
      from: 'Your applications are sent from',
      preparing: "We're setting up the address your applications will be sent from.",
      forwardBefore: "When a company replies, we'll forward the email to ",
      forwardAfter: '.',
      forwardOff:
        'Reply forwarding to your email is turned off. Write to us if you want to turn it back on.',
      noInbox: "You don't need to check another inbox or connect your Google account.",
    },
    checkout: {
      pending: 'Confirming your payment... one moment.',
      slow: "We got your subscription. If your plan doesn't show as active yet, reload the page in a minute, or write to us and we'll look into it with you.",
      activeStrong: 'Your plan is active.',
      activeAfter:
        ' Starting today we apply for you every business day, and replies come straight to your email.',
    },
    plan: {
      activeAfter: ' is active',
      renews: (date: string) => `, renews on ${date}`,
      dailyLimit: (n: number) => `We apply for you up to ${n} times every business day`,
      trialUntil: (date: string) => `Free trial until ${date}`,
      trialActive: 'Free trial active',
      trialThen: (price: string) => `. When it ends, you're charged ${price} a month`,
      trialManage: 'Manage or cancel',
    },
    expired: {
      headline: (date: string) => `Your plan expired on ${date}`,
      headlineNoDate: 'Your plan is no longer active',
      lede: "Reactivate it and we'll get back to applying for you every business day.",
    },
    generatingDrafts: 'Preparing your first applications...',
    weekend:
      "It's the weekend. Applications resume on Monday. Emails sent over the weekend are rarely read.",
    drafts: {
      titleActive: 'Your applications are ready',
      titleDemo: 'See how your application arrives',
      noteActive: "These go out to employers in the next send. You don't have to do anything.",
      noteDemo:
        'Send each one to your own email and you will see exactly what an employer gets: the subject, the message and your resume as a PDF.',
      startSending: 'Start sending to employers',
      startNote: 'From there we take it: we apply for you every business day.',
      activationFailed: "We couldn't turn on sending. Please try again in a moment.",
      activationConnection: 'Check your connection and try again.',
    },
    draftCard: {
      sent: 'Sent to your email. Take a look',
      send: 'Send to my email',
      ready: 'Ready to go',
    },
    freeTier: {
      ribbon: 'With the plan, TBit picks the jobs that fit you and applies for you every business day.',
      headline: 'You apply. With the plan, we apply for you.',
      lede: 'TBit applies for you every business day, with a letter for each employer written from your resume.',
      recentEmptyNote: 'Apply to a job from the list and you will see it here.',
    },
    jobsForYou: {
      title: 'Jobs for you',
      quota: (used: number, limit: number) => `${used} of ${limit} today`,
      starts: (date: string) => `starts ${date}`,
      applyGmail: 'Apply with my Gmail',
      applyCopy: 'Apply from my email',
      closed: 'This employer already closed this job.',
      applied: 'You applied today',
      footer: 'We pick them by your resume, your trade and your countries.',
      lockedLede: (n: number) =>
        n === 1
          ? 'We found 1 job that fits your resume. With the plan we tell you which one it is and apply for you.'
          : `We found ${n} jobs that fit your resume. With the plan we tell you which ones they are and apply for you.`,
      lockedSearchHint: 'Searching yourself, you apply one by one from your email. With the plan we pick them and apply for you.',
      lockedSearchSelf: "I'd rather search myself",
      lockedCta: 'See my recommended jobs',
      lockedRow: 'Recommended job',
      appliedTodayTitle: (n: number) => (n === 1 ? 'Today we applied for you to 1 job' : `Today we applied for you to ${n} jobs`),
      appliedTodayChip: 'Sent',
      recommendedTitle: 'Recommended for you',
      saved: {
        title: 'Saved',
        lede: 'The jobs you saved to look at later.',
        save: 'Save job',
        unsave: 'Remove from saved',
        failed: "We couldn't save the change. Try again.",
        tooMany: 'You already have 200 saved jobs. Remove one to save another.',
      },
      search: {
        title: 'Search jobs',
        lede: 'Every job for your trade and your countries, with or without a plan.',
        placeholder: 'Trade, city or company. For example: welder in Texas',
        submit: 'Search',
        newest: 'Newest',
        mix: 'A mix to get you started',
        pay: 'Best hourly pay',
        soon: 'Starting soon',
        filters: {
          program: 'Program',
          anyProgram: 'All programs',
          programs: { h2b: 'H-2B', h2a: 'H-2A', jobbank: 'Canada', eures: 'Europe' },
          region: 'Location',
          anyRegion: 'Anywhere',
          wage: 'Hourly pay',
          anyWage: 'Any pay',
          wageAtLeast: (n: number) => `From $${n} an hour`,
          start: 'Starts',
          anyStart: 'Any time',
          starts: { month: 'Within a month', quarter: 'In 1 to 3 months', later: 'In more than 3 months' },
          housing: 'Housing provided',
          sort: 'Sort',
          sorts: { mix: 'Mix', newest: 'Newest', pay: 'Best hourly pay', soon: 'Starting soon' },
          clear: 'Clear filters',
        },
        results: (q: string) => `Results for "${q}"`,
        more: 'See more',
        empty: "We didn't find jobs for that. Try other words.",
        failed: "We couldn't search. Try again in a moment.",
        loading: 'Searching',
        alreadyApplied: 'You already applied',
      },
      empty: 'No jobs for your profile yet. When they arrive, they show up here.',
      failed: "We couldn't load the jobs. Reload the page in a moment.",
      loading: 'Looking for jobs for you',
      jobBank: 'Job Bank with LMIA',
    },
    manualSheet: {
      label: 'Your application',
      template: 'Template',
      noteGmail: 'It goes out from your Gmail with your resume as a PDF.',
      noteCopy: 'You send it from your email. Attach your resume as a PDF.',
      loading: 'Preparing your application',
      to: 'To:',
      subject: 'Subject:',
      planTitle: "The plan's letter, for this job",
      planCompany: (company: string, date: string) =>
        date ? `Names ${company} and the ${date} start date.` : `Names ${company}.`,
      planYears: (years: number | null) =>
        years
          ? `Tells your ${years} ${years === 1 ? 'year' : 'years'} of experience using what your resume says.`
          : 'Tells your experience using what your resume says.',
      planAuto: 'Goes out on its own, without you having to log in.',
      quotaLine: (count: number, limit: number) => `This application counts toward today's limit: ${count} of ${limit}.`,
      quotaShared: 'The ones TBit sends for you and yours come out of the same limit.',
      sendGmail: 'Send from my Gmail',
      sendCopy: 'Copy and open my email',
      sent: 'Sent. If the employer replies, it reaches your Gmail.',
      copied: 'We copied the text. Paste it into a new email to this address and attach your resume:',
      copyFailed: "We couldn't copy it for you. Select the text, copy it, and send it to this address:",
      openMail: 'Open my email',
      dailyLimit: (limit: number) => `You reached your ${limit} for today. You can keep going tomorrow.`,
      noEmail: "This job doesn't include an email to apply to.",
      notActive: 'This job is no longer active.',
      paused: "Your sending is paused. Write to us and we'll go over it with you.",
      termsRequired: 'Before you apply, accept the TBit Jobs terms.',
      termsOpen: 'Accept the terms',
      alreadyApplied: 'You already applied to this job.',
      reconnect: 'Google is asking you to reconnect your Gmail to send from it.',
      cvRequired: 'Upload your resume to apply.',
      cvLink: 'Upload my resume',
      failed: "We couldn't send it. Try again in a moment.",
      previewFailed: "We couldn't prepare the application. Try again.",
      close: 'Close',
    },
    missedWeek: {
      label: 'What you missed this week',
      title: (postings: number, applied: number) =>
        `${postings === 1 ? '1 job' : `${postings} jobs`} for your profile arrived this week. You applied to ${applied}.`,
      withPlan: 'With the plan they go out on their own, each with a letter for that employer.',
      replies: (n: number) => (n === 1 ? '1 employer replied to you.' : `${n} employers replied to you.`),
      repliesAfter: 'With the plan, TBit tells you which ones are an opportunity and what to reply in English.',
    },
    countries: {
      title: 'Where we apply for you',
      lede: 'Pick one or more. This changes which job openings your applications go to.',
      groupLabel: 'Countries we apply to',
      options: {
        US: 'United States',
        CA: 'Canada',
        EU: 'Europe',
      },
      caNote:
        'Canada: Job Bank openings with an LMIA that accept candidates from abroad. Your work permit comes from that LMIA.',
      euNote:
        'Europe: EURES openings. Without EU papers we apply to the ones that say they accept candidates from outside the EU and to trades with a shortage work permit in Spain (electricians, aluminium, metal or PVC carpentry, crane operators and merchant navy), even if you have no papers.',
      lastOne: 'At least one country always stays checked.',
      nextBatch: 'The change applies to your next applications. The ones already ready stay as they are.',
      save: 'Save countries',
      saved: (names: string) => `Done. From now on we apply in ${names}.`,
      join: (names: string[]) =>
        names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : (names[0] ?? ''),
      saveFailed: "We couldn't save your countries. Please try again.",
      connectionError: 'Connection error. Please try again.',
    },
    demoErrors: {
      unavailable:
        "We couldn't send it right now. Your application is still ready, try again in a few minutes.",
      throttled: (minutes: number) =>
        `You're going fast. You can send another one in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}.`,
      failed: 'Something got in the way while sending it. Your application is still ready, try again.',
    },
    stats: {
      today: 'Today',
      remaining: (n: number) => `${n} left`,
      pending: 'Pending',
      replied: 'Replies',
      interviews: 'Interviews',
      ready: 'Ready',
    },
    recent: {
      title: 'Recent applications',
      emptyTitle: 'No applications sent yet',
      emptyNoteAuto: "We'll send applications for you in the next cycle.",
      emptyNoteDemo: 'Send yourself an application to see how it reaches an employer.',
      subject: 'Subject',
      status: 'Status',
      date: 'Date',
    },
  },

  history: {
    title: 'Your applications',
    filterLabel: 'Filter by status',
    status: {
      SENT: 'Sent',
      REPLIED: 'Replied',
      INTERVIEW: 'Interview',
      REJECTED: 'Rejected',
      QUEUED: 'Queued',
      NO_RESPONSE: 'No response',
    },
    filter: {
      all: 'All statuses',
      SENT: 'Sent',
      REPLIED: 'Replied',
      INTERVIEW: 'Interview',
      REJECTED: 'Rejected',
      QUEUED: 'Queued',
      NO_RESPONSE: 'No response',
    },
    httpError: (status: number) => `Error ${status}`,
    loadError: "Couldn't load your applications.",
    emptyFilteredTitle: 'Nothing with this status',
    emptyFilteredNote: 'There are no applications with this status.',
    emptyTitle: 'No applications sent yet',
    emptyNote: "Once your resume and preferences are set up, your applications will show up here.",
    company: 'Company',
    position: 'Position',
    statusHeader: 'Status',
    date: 'Date',
    total: (n: number) => `${n} ${n === 1 ? 'application' : 'applications'} in total`,
    previous: 'Previous',
    next: 'Next',
  },

  inbox: {
    title: 'Messages',
    lede: 'Replies from employers and invitations from agencies, all in one place.',
    loadError: "We couldn't load your messages. Reload the page.",
    loadMoreError: "We couldn't load more conversations. Please try again.",
    filterLabel: 'Filter messages',
    filters: {
      all: 'All',
      unread: 'Unread',
      invitations: 'Invitations',
    },
    loading: 'Loading your messages',
    empty: {
      all: {
        title: 'No conversations yet',
        note: "When an employer replies to one of your applications, you'll see it here.",
      },
      unread: {
        title: "You're all caught up",
        note: 'You have no unopened replies or invitations.',
      },
      invitations: {
        title: 'No invitations yet',
        note: "When an agency wants to talk to you, we'll let you know here and by email.",
      },
    },
    seeApplications: 'See my applications',
    activateTalentPool: 'Join the talent pool',
    loadMore: 'See older conversations',
    gmailReconnect: 'To see the replies that reach your Gmail here, reconnect your account.',
    reconnectGmail: 'Reconnect Gmail',
  },

  thread: {
    loading: 'Opening the conversation',
    loadError: "We couldn't load this conversation. Reload the page.",
    scope: 'Messages',
    fallbackTitle: 'Conversation',
    notFoundTitle: "We couldn't find this conversation",
    notFoundNote: 'The link may belong to another account, or the application may no longer exist.',
    seeMessages: 'See my messages',
    back: 'Back',
    opportunity: 'Opportunity',
    appliedOn: (date: string) => `You applied on ${date}`,
    wroteTo: (email: string) => ` · we wrote to ${email}`,
    reply: 'Reply',
    gmailTitle: 'This conversation lives in your Gmail',
    gmailNote:
      'Your applications are sent from your own Google account, so replies land in your inbox. Open it in Gmail to answer the company: that way the company sees you, not a middleman.',
    openInGmail: 'Open in Gmail',
    gmailReplyNote: (address: string) =>
      address
        ? `This conversation is with your Gmail: your reply goes out from your own email (${address}).`
        : 'This conversation is with your Gmail: your reply goes out from your own email.',
    gmailReconnectTitle: 'Reconnect your Gmail',
    gmailReconnectNote:
      "Google no longer lets us read this conversation. Reconnect your account to see it and reply from here; meanwhile you can open it in Gmail.",
    reconnectGmail: 'Reconnect Gmail',
    gmailUnavailableTitle: "We couldn't read your Gmail right now",
    gmailUnavailableNote: 'Try again in a few minutes, or open the conversation in Gmail.',
    retry: 'Try again',
    status: {
      SENT: 'Sent',
      REPLIED: 'Replied',
      INTERVIEW: 'Interview',
      REJECTED: 'Rejected',
      NO_RESPONSE: 'No response',
    },
  },

  invitation: {
    accepted: 'You accepted',
    declined: 'You declined',
    notShared: "We didn't share your details with this agency.",
  },

  invitations: {
    loadError: "We couldn't load your invitations. Reload the page.",
    connection: 'Connection error. Please try again.',
    alreadyAnswered: 'This invitation was already answered.',
    respondFailed: "We couldn't save your answer.",
    acceptedNotice: (agency: string) =>
      `We shared your details with ${agency}. They'll write to you directly.`,
    declinedNotice: "Done, we didn't share anything. The agency only knows you're not interested.",
    loading: 'Loading your invitations',
    focusedTitle: 'An agency wants to contact you',
    title: 'Invitations',
    seeAll: 'See all',
    notFoundTitle: "We couldn't find this invitation",
    notFoundNote: 'The link may belong to another account, or the agency may have withdrawn it.',
    seeMine: 'See my invitations',
    emptyTitle: 'No invitations yet',
    emptyNote:
      "When an agency wants to talk to you, we'll let you know here and by email. To get found, join the talent pool in your profile.",
    activateTalentPool: 'Join the talent pool',
    noPending: "You have no pending invitations. Your history is below.",
    agency: 'Agency:',
    invitedOn: (date: string) => `Invited on ${date}`,
    pendingNote:
      "They saw your profile without your personal details and want to talk to you. If you accept, we'll give them your name and email so they can write to you. If not, we don't share anything.",
    decline: 'No, thanks',
    accept: 'Yes, they can contact me',
    sharedOn: (agency: string, date: string) => `We shared your details with ${agency} on ${date}.`,
  },

  threadItem: {
    you: 'You: ',
    unread: (n: number) => `${n} unread`,
    replied: 'Replied',
    opportunity: 'Opportunity',
  },

  invitationItem: {
    label: 'Invitation',
    pendingPreview:
      "An agency saw your profile without your details and wants to contact you. You decide whether we give them your email.",
    sharedWith: (agency: string) => `We shared your details with ${agency}.`,
    unanswered: 'Not answered',
  },

  recentMessages: {
    scope: 'Inbox',
    title: 'Recent messages',
    seeAll: 'See all',
    emptyTitle: 'No conversations yet',
    emptyNote: "When an employer replies, you'll see it here.",
  },

  composer: {
    label: 'Write your reply',
    placeholder: 'Write your reply to the company…',
    retry: 'Try again',
    tooLong: (n: number) => `You're ${n} ${n === 1 ? 'character' : 'characters'} over the limit.`,
    to: (email: string) => `Goes to ${email}`,
    shortcut: 'Ctrl + Enter to send',
    sending: 'Sending…',
    send: 'Send reply',
  },

  suggestion: {
    scope: 'Suggested reply',
    loading: 'Reading what the company wrote…',
    typology: {
      interest: "They're interested in your profile",
      interview: 'They want to talk to you',
      info_request: "They're asking for more information",
      locals_only: 'For now they only hire inside the U.S.',
      rejection: "They didn't move forward with your application",
      other: 'Another kind of reply',
    } as Record<string, string>,
    optionsTitle: 'What do you want to answer?',
    optionsNote: "Tap one and we'll write drafts for it.",
    replyOptions: {
      interview_confirm: 'Confirm availability',
      interview_reschedule: 'Suggest another time',
      interview_details: 'Ask for the interview details',
      info_send: 'Send what they ask for',
      info_clarify: 'Ask exactly which documents',
      interest_confirm: 'Confirm interest and availability',
      interest_question: 'Ask a question about the job',
      locals_visa_next_season: "Explain I'm looking for the visa and ask about the next hiring round",
      locals_keep_on_file: 'Ask them to keep me in mind',
      locals_transfer: "Say I'm in the U.S. on H-2 and available for a transfer",
      rejection_future: 'Thank them and ask to be considered next season',
    } as Record<string, string>,
    optionLoading: 'Writing drafts for that option…',
    draftsTitle: 'You could answer like this',
    languageNote: 'Drafts are in the language the company wrote to you in.',
    option: (n: number) => `Option ${n}`,
    use: 'Use this reply',
    used: "It's in your reply now. Review it before sending.",
    note: 'Nothing is sent on its own: the text goes into your reply so you can review it and change anything you want.',
    regenerate: 'See other options',
    retry: 'Try again',
    errors: {
      failed: "We couldn't prepare a suggestion right now. You can write your reply or try again.",
      rateLimited: (minutes: number) =>
        `You asked for several suggestions in a row. Try again in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}.`,
      network: "We couldn't connect. Check your connection and try again.",
    },
  },

  bubble: {
    application: 'Application sent',
    you: 'You',
    company: 'The company',
    new: 'New',
    sendingLabel: 'Sending',
    cvAttached: 'Your resume was attached',
    sending: 'Sending…',
  },

  time: {
    now: 'just now',
    minutes: (n: number) => `${n} min ago`,
    hours: (n: number) => `${n} h ago`,
    yesterday: 'yesterday',
    days: (n: number) => `${n} days ago`,
  },

  replyErrors: {
    network: "We couldn't send your reply. Check your connection and try again.",
    gmailChannel: 'This thread lives in your Gmail. Open it there to reply to the company.',
    rateLimited: (minutes: number) =>
      `You sent several replies in a row. Try again in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}.`,
    bounced:
      "The company's email bounced: it doesn't exist or doesn't accept messages. Your text is still here.",
    sendFailed: "We couldn't deliver your reply. Your text is still here, please try again.",
    threadNotSent: "This application hasn't been sent yet, so there's no conversation yet.",
    noContactEmail: "This job didn't leave a contact email, so there's nowhere to reply.",
    senderNotProvisioned:
      "We're still setting up your sending address. Try again in a few minutes.",
    notFound: 'This thread is no longer available.',
    gmailReconnect: 'Reconnect your Gmail to reply from here. Your text is still here.',
    gmailUnavailable: "Gmail didn't respond. Your text is still here, please try again.",
    gmailThreadMissing: "This conversation is no longer in your Gmail, so there's no thread to reply to.",
    recipientChanged:
      'The recipient of your reply changed. Check who it goes to below and send again: your text is still here.',
    replyInProgress: 'Your reply is already being sent. Wait a moment before trying again.',
    generic: "We couldn't send your reply. Please try again.",
  },
};

export default workspace;
